<template>
    <card-grid-item
        :tab-widget="tabWidget"
        :card-class="['card-grid']"
        :min-w="3"
        :min-h="minHeight"
        :resized="handleResized"
        @tab-widget-settings-click="handleTabWidgetSettingsClick"
    >
        <template v-slot:card-title>
            {{ title }}
        </template>
        <template v-slot:card-body>
            <busy-modal :show="busy" class="chart-widget-spinner" />
            <vue-apex-charts v-if="ready" height="350" :options="options" :series="series" />
        </template>
        <template v-slot:tab-widget-settings-modal>
            <tab-widget-settings-modal-form
                :modal-id="tabWidgetSettingsModalID"
                :update="updateTabWidgetSettings"
                :initial-values="tabWidgetSettingsInitialValues"
                :with-chart-interval="true"
                :with-daily-chart-interval="true"
                :with-yearly-chart-interval="false"
                :with-chart-period="true"
                @success-update="handleSuccessTabWidgetSettingsUpdate"
            ></tab-widget-settings-modal-form>
        </template>
    </card-grid-item>
</template>

<script>
import API from '../../../../api';
import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
import CardGridItem from '../common/card-grid-item.vue';
import DeparturesCardGrid from '../common/departures-card-grid.vue';
import { DASHBOARD_STAYLIO_PUSH_NOTIFICATION_METRICS_ENDPOINT } from '../../../../endpoints';
import GoBackIconButton from '../../../generic/go-back-icon-button/go-back-icon-button.vue';
import {
    getWidgetGridRowsNumber,
    getApexChartsGradientFill,
    DEFAULT_DASHBOARD_CHART_INTERVAL,
    getDefaultChartPeriodBasedOnInterval,
} from '../helpers';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import TabWidgetSettingsModalForm from '../common/tab-widget-settings-modal-form.vue';
import VueApexCharts from 'vue3-apexcharts';
import { format } from 'date-fns';
import cardGridItemBaseMixin from '../common/card-grid-item-base-mixin';

export default {
    mixins: [cardGridItemBaseMixin],
    name: 'StaylioPushNotificationMetricsChartCardGridItem',
    components: {
        BusyModal,
        CardGridItem,
        TabWidgetSettingsModalForm,
        VueApexCharts,
        ResponsiveButton,
        GoBackIconButton,
        DeparturesCardGrid,
    },
    data() {
        return {
            autoWidthExclude: ['guest', 'property_name'],
            busy: null,
            chartData: [],
            selectedDay: null,
            limit: getWidgetGridRowsNumber({
                tabWidget: this.tabWidget,
            }),
            dates: [],
        };
    },
    computed: {
        tabWidgetSettingsInitialValues() {
            return {
                settings: {
                    name: this.tabWidget.settings.name || this.defaultName,
                    chart_period:
                        this.tabWidget.settings.chart_period ||
                        getDefaultChartPeriodBasedOnInterval(DEFAULT_DASHBOARD_CHART_INTERVAL),
                    chart_interval: this.tabWidget.settings.chart_interval || DEFAULT_DASHBOARD_CHART_INTERVAL,
                },
            };
        },
        series() {
            return this.chartData.map((item) => {
                return {
                    name: item.name,
                    data: item.count,
                };
            });
        },
        options() {
            return {
                chart: {
                    type: 'area',
                    toolbar: {
                        show: false,
                    },
                    zoom: {
                        enabled: false,
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    curve: 'straight',
                },
                fill: getApexChartsGradientFill(),
                grid: {
                    xaxis: {
                        lines: {
                            show: true,
                        },
                    },
                },
                xaxis: {
                    type: 'datetime',
                    categories: this.dates,
                    labels: {
                        datetimeFormatter: {
                            month: 'MMM',
                            day: 'MMM dd',
                        },
                    },
                },
                yaxis: {
                    labels: {
                        formatter: (val) => `${val}`,
                    },
                },
                tooltip: {
                    shared: true,
                    intersect: false,
                    custom: (params) => {
                        const { series, seriesIndex, dataPointIndex, w } = params;
                        const day = this.dates[dataPointIndex].split('-')[2];
                        const month = this.dates[dataPointIndex].split('-')[1] - 1;
                        const year = this.dates[dataPointIndex].split('-')[0];
                        const interval = this.tabWidget.settings.chart_interval || DEFAULT_DASHBOARD_CHART_INTERVAL;
                        let dateFormat = 'dd MMM, yyyy';

                        if (interval == 'YEARLY') {
                            dateFormat = 'yyyy';
                        } else if (interval == 'MONTHLY') {
                            dateFormat = 'MMM yyyy';
                        }

                        const date = format(new Date(year, month, day), dateFormat);

                        const filterData = this.chartData.map((item) => {
                            return {
                                name: item.name,
                                count: item.count[dataPointIndex],
                            };
                        });

                        return `
                                <div class="apexcharts-tooltip-title" style="font-size:12px;">${date}</div>
                                <div style="padding:5px; font-size:12px; color: #111;">
                                    ${filterData
                                        .map((item, index) => {
                                            return `
                                            <div style="padding:5px;">
                                                <span style="background:${
                                                    w.globals.colors[index] || '#000'
                                                }; width:10px; height:10px; border-radius:50%; display: inline-block; margin-right: 5px;"></span>
                                                <span>${item.name}: <b style="padding-left:5px;">${
                                                item.count
                                            }</b></span>
                                            </div>
                                        `;
                                        })
                                        .join('')}
                                        </div>
                            `;
                    },
                },
            };
        },
    },
    mounted() {
        this.fetchChartData();
    },
    methods: {
        async fetchChartData() {
            try {
                this.busy = true;
                const interval = this.tabWidget.settings.chart_interval || DEFAULT_DASHBOARD_CHART_INTERVAL;
                const period = this.tabWidget.settings.chart_period || getDefaultChartPeriodBasedOnInterval(interval);

                const response = await API.get(DASHBOARD_STAYLIO_PUSH_NOTIFICATION_METRICS_ENDPOINT(), {
                    params: {
                        period: period,
                        interval: interval,
                    },
                });

                this.dates = response.data.map((item) => item.date);

                const metrics = [
                    'guest_push_notifications_sent',
                    'guest_push_notifications_delivered',
                    'guest_push_notifications_opened',
                    'guest_push_notifications_failed_to_deliver',
                ];
                const names = ['Sent', 'Delivered', 'Opened', 'Failed to Deliver'];

                const list = metrics.map((metric, index) => ({
                    name: names[index],
                    count: response.data.map((item) => item[metric]),
                }));

                this.chartData = list;
            } catch (e) {
                console.log(e);
                this.errorToast({ text: 'Could not fetch chart data' });
            } finally {
                this.busy = false;
            }
        },
        handleResized() {
            this.limit = getWidgetGridRowsNumber({ tabWidget: this.tabWidget });
            this.cardGridKey += 1;
        },
    },
};
</script>
